import type { ParsedRole } from '@rhia/protocol';
import {
  createUserWithEmailAndPassword,
  type User as FbUser,
  signOut as fbSignOut,
  GoogleAuthProvider,
  getRedirectResult,
  OAuthProvider,
  onAuthStateChanged,
  SAMLAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  updateProfile,
} from 'firebase/auth';
import {
  collectionGroup,
  doc,
  getDoc,
  onSnapshot,
  query,
  type Unsubscribe,
  where,
} from 'firebase/firestore';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiFetch } from '@/lib/api';
import { getAuthInstance, getDbInstance } from '@/lib/firebase';

const ROLE_RANK: Record<ParsedRole, number> = {
  super_admin: 100,
  workspace_admin: 80,
  engineer: 60,
  operator: 40,
  viewer: 20,
};

/**
 * Shape of the user surface exposed via `state.current.user`. This is a
 * Firebase-flavoured subset of the legacy `ParsedUser` shape — only the
 * fields the editor actually consumes are populated. Pre-Firebase
 * fields (id, avatar_url, created_at_ms, updated_at_ms) are omitted.
 */
export type ApprovalStatus = 'pending' | 'approved';

export type AuthUser = {
  uid: string;
  email: string | null;
  display_name: string;
  is_super_admin: boolean;
  /**
   * True when the Firebase ID-token custom claim `console === true` is set.
   * Grants access to the rhia-com download page and console-gated API endpoints.
   * Super-admins are implicitly granted console access too.
   */
  has_console: boolean;
  /**
   * Self-serve sign-ups are 'pending' until an admin approves them; the app
   * gates them onto a holding screen. Sourced live from users/{uid}; missing
   * ⇒ 'approved' (legacy/seed users predate this field).
   */
  approval_status: ApprovalStatus;
};

/**
 * Membership shape exposed via `state.current.memberships`. Sourced from
 * the Firestore subcollection `workspaces/{wid}/memberships/{uid}` via a
 * collectionGroup listener.
 */
export type AuthMembership = {
  workspace_id: string;
  role: Exclude<ParsedRole, 'super_admin'>;
};

export type CurrentAuth = {
  user: AuthUser;
  memberships: AuthMembership[];
};

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; current: CurrentAuth };

export type AuthContextValue = {
  state: AuthState;
  /** Sign in with email + password via Firebase Auth. */
  login: (email: string, password: string) => Promise<void>;
  /** Sign in with Google (popup). New accounts are bootstrapped as pending. */
  loginWithGoogle: () => Promise<void>;
  /**
   * Sign in via a per-tenant SSO provider (SAML or OIDC) using a redirect
   * flow. `providerId` is `saml.<wid>` or `oidc.<wid>` from the resolve
   * endpoint. A brand-new SSO user's profile is bootstrapped on return via
   * `getRedirectResult` (see `AuthProvider` mount effect).
   */
  loginWithSso: (providerId: string) => Promise<void>;
  /**
   * Self-serve sign-up with email + password. Creates the Firebase account,
   * sets the display name, then bootstraps a pending profile server-side.
   */
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  /** Sign out via Firebase Auth. */
  logout: () => Promise<void>;
  /**
   * Force a fresh ID token (refreshes custom claims) and re-derive
   * super_admin from the new token.
   */
  refresh: () => Promise<void>;
  /** Returns the user's role in the workspace, or null if not a member. */
  roleIn: (workspaceId: string) => ParsedRole | null;
  /** True if the user has at least the given role in the workspace. */
  hasRole: (workspaceId: string, min: ParsedRole) => boolean;
  isSuperAdmin: () => boolean;
};

const AuthCtx = createContext<AuthContextValue | undefined>(undefined);

async function buildUser(
  fb: FbUser,
  approvalStatus: ApprovalStatus,
  forceRefresh = false,
): Promise<AuthUser> {
  const tokenResult = await fb.getIdTokenResult(forceRefresh);
  const claims = tokenResult.claims as { is_super_admin?: boolean; console?: boolean };
  return {
    uid: fb.uid,
    email: fb.email,
    display_name: fb.displayName ?? fb.email ?? fb.uid,
    is_super_admin: claims.is_super_admin === true,
    has_console: claims.console === true,
    approval_status: approvalStatus,
  };
}

/** Read users/{uid}.approval_status once; missing ⇒ 'approved'. */
async function readApproval(uid: string): Promise<ApprovalStatus> {
  try {
    const snap = await getDoc(doc(getDbInstance(), 'users', uid));
    return (snap.data() as { approval_status?: string } | undefined)?.approval_status === 'pending'
      ? 'pending'
      : 'approved';
  } catch {
    return 'approved';
  }
}

/** POST /api/auth/signup — idempotently ensures a (pending) profile exists. */
async function bootstrapProfile(displayName?: string): Promise<void> {
  await apiFetch('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ display_name: displayName ?? null }),
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const membershipsUnsubRef = useRef<Unsubscribe | null>(null);
  const approvalUnsubRef = useRef<Unsubscribe | null>(null);
  // Latest Firebase user kept in a ref so refresh() and the membership
  // listener can read the current uid without retriggering effects.
  const fbUserRef = useRef<FbUser | null>(null);

  const stopMembershipListener = useCallback(() => {
    if (membershipsUnsubRef.current) {
      membershipsUnsubRef.current();
      membershipsUnsubRef.current = null;
    }
    if (approvalUnsubRef.current) {
      approvalUnsubRef.current();
      approvalUnsubRef.current = null;
    }
  }, []);

  // On mount: check if we're returning from a signInWithRedirect (SSO flow).
  // If so, bootstrap a pending profile for brand-new SSO users.
  // onAuthStateChanged fires before getRedirectResult resolves, so by the
  // time this runs the user is already in state; bootstrapProfile is
  // idempotent, so calling it on every redirect return is safe.
  useEffect(() => {
    getRedirectResult(getAuthInstance())
      .then(async (result) => {
        if (result?.user) {
          await bootstrapProfile();
        }
      })
      .catch((err) => console.warn('[auth] getRedirectResult failed', err));
    // Run once on mount — no dependencies needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to Firebase auth state. On sign-in: build user, subscribe to
  // memberships. On sign-out: tear down memberships listener.
  useEffect(() => {
    const auth = getAuthInstance();
    const unsubAuth = onAuthStateChanged(auth, async (fb) => {
      fbUserRef.current = fb;
      stopMembershipListener();
      if (!fb) {
        setState({ status: 'unauthenticated' });
        return;
      }
      try {
        // Read approval once up front so the first paint gates correctly (no
        // flash of the app for a pending user, or the pending screen for an
        // approved one); the listener below keeps it live.
        const approval = await readApproval(fb.uid);
        const user = await buildUser(fb, approval);
        // Seed authenticated state with empty memberships; the listener
        // below will populate them.
        setState({ status: 'authenticated', current: { user, memberships: [] } });
        const db = getDbInstance();
        // Live approval: when an admin approves, the holding screen clears
        // without the user having to sign out and back in.
        approvalUnsubRef.current = onSnapshot(
          doc(db, 'users', fb.uid),
          (snap) => {
            const next: ApprovalStatus =
              (snap.data() as { approval_status?: string } | undefined)?.approval_status ===
              'pending'
                ? 'pending'
                : 'approved';
            setState((prev) =>
              prev.status === 'authenticated'
                ? {
                    status: 'authenticated',
                    current: {
                      ...prev.current,
                      user: { ...prev.current.user, approval_status: next },
                    },
                  }
                : prev,
            );
          },
          (err) => console.warn('[auth] approval listener error', err),
        );
        const q = query(collectionGroup(db, 'memberships'), where('uid', '==', fb.uid));
        membershipsUnsubRef.current = onSnapshot(
          q,
          (snap) => {
            const memberships: AuthMembership[] = snap.docs.map((d) => {
              const data = d.data() as { role: AuthMembership['role'] };
              // Path: workspaces/{wid}/memberships/{uid}
              const workspaceId = d.ref.parent.parent?.id ?? '';
              return { workspace_id: workspaceId, role: data.role };
            });
            setState((prev) =>
              prev.status === 'authenticated'
                ? { status: 'authenticated', current: { ...prev.current, memberships } }
                : prev,
            );
          },
          (err) => {
            // Listener errors (e.g. permission denied before claims
            // propagate) shouldn't bounce the user back to the login
            // screen — leave auth state alone and keep memberships empty.
            console.warn('[auth] memberships listener error', err);
          },
        );
      } catch (err) {
        console.warn('[auth] buildUser failed', err);
        setState({ status: 'unauthenticated' });
      }
    });
    return () => {
      unsubAuth();
      stopMembershipListener();
    };
  }, [stopMembershipListener]);

  const login = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(getAuthInstance(), email, password);
    // onAuthStateChanged will pick up the new user and update state.
  }, []);

  const loginWithGoogle = useCallback(async () => {
    await signInWithPopup(getAuthInstance(), new GoogleAuthProvider());
    // Idempotently ensure a profile exists (a brand-new Google account gets a
    // pending profile; a returning user just gets their existing status back).
    await bootstrapProfile();
    // onAuthStateChanged will refresh state with the (possibly pending) account.
  }, []);

  const loginWithSso = useCallback(async (providerId: string) => {
    const provider = providerId.startsWith('saml.')
      ? new SAMLAuthProvider(providerId)
      : new OAuthProvider(providerId);
    await signInWithRedirect(getAuthInstance(), provider);
    // signInWithRedirect navigates the page away; the getRedirectResult effect
    // above picks up the result on return and calls bootstrapProfile().
  }, []);

  const signup = useCallback(async (email: string, password: string, displayName: string) => {
    const cred = await createUserWithEmailAndPassword(getAuthInstance(), email, password);
    if (displayName.trim()) {
      await updateProfile(cred.user, { displayName: displayName.trim() });
    }
    await bootstrapProfile(displayName);
    // onAuthStateChanged will pick up the new (pending) account.
  }, []);

  const logout = useCallback(async () => {
    await fbSignOut(getAuthInstance());
    // onAuthStateChanged will set state to 'unauthenticated'.
  }, []);

  const refresh = useCallback(async () => {
    const fb = fbUserRef.current;
    if (!fb) {
      setState({ status: 'unauthenticated' });
      return;
    }
    try {
      const approval = await readApproval(fb.uid);
      const user = await buildUser(fb, approval, true);
      setState((prev) =>
        prev.status === 'authenticated'
          ? { status: 'authenticated', current: { ...prev.current, user } }
          : { status: 'authenticated', current: { user, memberships: [] } },
      );
    } catch (err) {
      console.warn('[auth] refresh failed', err);
      setState({ status: 'unauthenticated' });
    }
  }, []);

  const roleIn = useCallback(
    (workspaceId: string): ParsedRole | null => {
      if (state.status !== 'authenticated') return null;
      if (state.current.user.is_super_admin) return 'super_admin';
      const m = state.current.memberships.find((mm) => mm.workspace_id === workspaceId);
      return m?.role ?? null;
    },
    [state],
  );

  const hasRole = useCallback(
    (workspaceId: string, min: ParsedRole) => {
      const r = roleIn(workspaceId);
      if (!r) return false;
      return ROLE_RANK[r] >= ROLE_RANK[min];
    },
    [roleIn],
  );

  const isSuperAdmin = useCallback(
    () => state.status === 'authenticated' && state.current.user.is_super_admin,
    [state],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      login,
      loginWithGoogle,
      loginWithSso,
      signup,
      logout,
      refresh,
      roleIn,
      hasRole,
      isSuperAdmin,
    }),
    [
      state,
      login,
      loginWithGoogle,
      loginWithSso,
      signup,
      logout,
      refresh,
      roleIn,
      hasRole,
      isSuperAdmin,
    ],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
